import { AppState } from "store/rootReducer";
import { createSelector } from "reselect";
import { AppConfig } from "api";

const creativesState = (state: AppState) => state.materialSpecs.specs;

export const getMaterials = createSelector(creativesState, ({ materialSpecificationId, materialSpecifications }) => ({
    materialSpecificationId,
    materialSpecifications,
}));

export const getAdDetails = createSelector(creativesState, ({ adDetails }) => adDetails);

export const getMaterialSpecificationDimensions = (materialSpecId: number) =>
    createSelector(creativesState, ({ materialSpecifications }) => {
        const matSpec = materialSpecifications.find(({ id }) => materialSpecId === id);

        return matSpec?.size;
    });

export const getAdMaterialsBySpecId = (materialSpecId: string) =>
    createSelector(creativesState, ({ materialSpecifications }) => {
        const matSpec = materialSpecifications.find(({ id }) => materialSpecId === id.toString());

        return matSpec?.materials || [];
    });

export const getMaterialSpecById = (materialSpecId?: number) =>
    createSelector(creativesState, ({ materialSpecifications }) => {
        const matSpec = materialSpecifications.find(({ id }) => materialSpecId === id);

        if (matSpec) {
            return matSpec;
        }
    });

export const getAllMaterialSpecifications = createSelector(
    creativesState,
    ({ materialSpecifications }) => materialSpecifications
);

export const getMaterialsProcessingStatus = createSelector(
    creativesState,
    ({ isProcessing, hasError, errorMessage }) => ({
        isProcessing,
        hasError,
        errorMessage,
    })
);

export const getOptionalUploadStatusById = (materialSpecId?: number) =>
    createSelector(creativesState, ({ materialSpecifications }) => {
        return materialSpecifications.some(
            ({ isUploadingOptional, id }) => isUploadingOptional && id === materialSpecId
        );
    });

export const getAdMaterialsThirdPartyTag = (materialSpecificationId: number | null, adMaterialId?: number | null) =>
    createSelector(creativesState, ({ materialSpecifications }) => {
        const matSpec = materialSpecifications.find(({ id }) => materialSpecificationId === id);

        if (matSpec) {
            const adMaterial = matSpec.materials.find(({ id }) => id === adMaterialId);

            if (adMaterial && adMaterial.tag) {
                return adMaterial.tag;
            }
        }

        return "";
    });

export const getMaterialDetailsById = (materialSpecificationId?: number, adMaterialId?: number) =>
    createSelector(creativesState, ({ materialSpecifications }) => {
        const matSpec = materialSpecifications.find(({ id }) => materialSpecificationId === id);

        if (matSpec) {
            const material = matSpec.materials.find(({ id }) => id === adMaterialId);

            return material || null;
        }

        return null;
    });

export const getPreflightReportStatus = createSelector(
    creativesState,
    ({ isPreflightReportLoading }) => isPreflightReportLoading
);

export const getThirdPartyStatus = createSelector(
    creativesState,
    ({ isThirdPartyProcessing }) => isThirdPartyProcessing
);

export const getMaterialSpecUpdatingStatus = (materialSpecificId: number) =>
    createSelector(creativesState, ({ materialSpecifications }) => {
        const materialSpec = materialSpecifications.find(({ id }) => materialSpecificId === id);

        if (materialSpec) {
            return materialSpec.isStatusUpdateProcessing;
        }

        return false;
    });

export const getMaterialSpecWorkingStatus = (materialSpecificId: number) =>
    createSelector(creativesState, ({ materialSpecifications }) => {
        const materialSpec = materialSpecifications.find(({ id }) => materialSpecificId === id);

        if (materialSpec) {
            return materialSpec.isWorking;
        }

        return false;
    });

export const checkUploadedMaterialsStatus = createSelector(creativesState, ({ materialSpecifications }) => {
    return materialSpecifications.some(({ materials }) => materials.some(({ isUploading }) => isUploading));
});

export const checkMaterialsDeleteStatus = createSelector(creativesState, ({ materialSpecifications }) => {
    return materialSpecifications.some(({ materials }) => materials.some(({ isDeleting }) => isDeleting));
});

export const getOrderNumber = createSelector(creativesState, ({ adDetails }) => adDetails?.orderNo);

export const getCreativeTypes = createSelector(creativesState, ({ materialSpecifications }) =>
    materialSpecifications
        .map(({ creativeType }) => creativeType)
        .filter((creativeType, index, self) => creativeType && self.indexOf(creativeType) === index)
);

export const getLineItemsHeader = createSelector(creativesState, ({ config }) => config.lineItemHeader.header);

export const getLineItemsRowById = (materialSpecId: number) =>
    createSelector(creativesState, ({ config }) => config.lineItemHeader.rows.find(({ id }) => materialSpecId === id));

export const getConfig = (key: keyof AppConfig) => createSelector(creativesState, ({ config }) => config[key]);

export const getAdMaterialSelectedPageById = (materialSpecId?: number, adMaterialId?: number) =>
    createSelector(creativesState, ({ materialSpecifications }) => {
        if (materialSpecId && adMaterialId) {
            const matSpec = materialSpecifications.find(({ id }) => materialSpecId === id);

            if (matSpec) {
                const material = matSpec.materials.find(({ id }) => adMaterialId === id);

                if (material && material.materialPagesPreview) {
                    return material.materialPagesPreview;
                }
            }
        }
    });

// TODO: remove this when JWT will start returning username property
export const getUserName = createSelector(creativesState, ({ user }) => user);

export const checkMaterialSpecContentAvailability = (materialSpecId: number) =>
    createSelector(creativesState, ({ materialSpecifications }) => {
        if (materialSpecId) {
            const materialSpec = materialSpecifications.find(({ id }) => materialSpecId === id);

            if (materialSpec) {
                return materialSpec.materials.every(({ inputFileName }) => inputFileName === "");
            }
        }

        return true;
    });

export const getMIMETypes = createSelector(creativesState, ({ config }) => config.mimeTypes);
